// Verhalten der neuen Produktseite (hy-main-product): Custom Elements für Galerie-Zähler, Stepper, Versandbalken, Countdown und Sticky-Leiste.
// Utils über die versionierte asset_url laden (link[data-hy-pdp-utils] in hy-main-product), damit nach einem Deploy
// nie ein gecachtes altes Modul mit einem neuen hy-pdp.js zusammentrifft.
const utilsUrl = document.querySelector('link[data-hy-pdp-utils]')?.href || new URL('./hy-pdp-utils.js', import.meta.url).href;
const {
  formatCountdown, isPromoActive, freeShippingProgress, lineTotalCents, remainingForFreeShipping, shippableCartTotalCents, variantQuantityInCart,
} = await import(utilsUrl);

const MS_PER_SECOND = 1000;
const COUNTDOWN_TICK_MS = 15 * MS_PER_SECOND;
const ADDED_FEEDBACK_MS = 1500;
const CART_BUMP_MS = 400;
const QUANTITY_EVENT = 'hy-pdp:quantity';

const formatMoney = (cents) => new Intl.NumberFormat(window.Shopify?.locale, {
  style: 'currency',
  currency: window.Shopify?.currency?.active || 'EUR',
}).format(cents / 100);

// Schwellen sind in Shop-Währung hinterlegt; bei anderer Kundenwährung mit dem Shopify-Kurs umrechnen.
const toPresentmentCents = (shopCents) => Math.round(shopCents * (Number(window.Shopify?.currency?.rate) || 1));

const formatMoneyWithoutTrailingZeros = (cents) => formatMoney(cents).replace(/[.,]00(?=\D*$)/, '');

document.querySelectorAll('[data-hy-pdp-money]').forEach((element) => {
  element.textContent = formatMoneyWithoutTrailingZeros(toPresentmentCents(Number(element.dataset.hyPdpMoney)));
});

const readQuantity = (formId) => {
  const input = document.querySelector(`#${CSS.escape(formId)} input[name="quantity"]`);
  return Math.max(1, Number.parseInt(input?.value, 10) || 1);
};

class HyPdpGalleryCounter extends HTMLElement {
  #abortController;

  connectedCallback() {
    const imageList = this.closest('.product')?.querySelector('.product-gallery__image-list');
    const carousel = imageList?.querySelector('scroll-carousel');
    if (!imageList || !carousel) return;

    if (this.parentElement !== imageList) {
      imageList.append(this);
      return; // append() verbindet das Element erneut
    }

    this.#abortController = new AbortController();
    carousel.addEventListener('carousel:change', (event) => this.#render(event.detail.index), { signal: this.#abortController.signal });
    this.#render(0);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #render(index) {
    this.textContent = `${index + 1} / ${this.getAttribute('count')}`;
  }
}

if (!customElements.get('hy-pdp-gallery-counter')) {
  customElements.define('hy-pdp-gallery-counter', HyPdpGalleryCounter);
}

class HyPdpPromo extends HTMLElement {
  #timer;

  connectedCallback() {
    this.#update();
    this.#timer = setInterval(() => this.#update(), COUNTDOWN_TICK_MS);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #seconds(name) {
    const value = this.dataset[name];
    return value ? Number(value) * MS_PER_SECOND : null;
  }

  #update() {
    const now = Date.now();
    const end = this.#seconds('end');
    const active = isPromoActive(now, this.#seconds('start'), end);
    this.hidden = !active;

    const countdown = this.querySelector('[data-countdown]');
    if (countdown && end) countdown.textContent = formatCountdown(end - now);

    if (end && now >= end) clearInterval(this.#timer);
  }
}

if (!customElements.get('hy-pdp-promo')) {
  customElements.define('hy-pdp-promo', HyPdpPromo);
}

/* Mengen-Stepper: meldet Mengenänderungen als hy-pdp:quantity und aktualisiert den Button-Text (ab 2 Stück mit Gesamtpreis). */
class HyPdpQuantity extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener('input', () => this.#announce(), { signal });
    this.addEventListener('change', () => this.#announce(), { signal });
    this.addEventListener('click', () => requestAnimationFrame(() => this.#announce()), { signal });
    document.addEventListener(QUANTITY_EVENT, (event) => this.#sync(event), { signal });
    this.#announce();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get #input() {
    return this.querySelector('input[name="quantity"]');
  }

  #announce() {
    const formId = this.getAttribute('form');
    const quantity = readQuantity(formId);
    this.#renderButton(quantity);
    document.dispatchEvent(new CustomEvent(QUANTITY_EVENT, { detail: { form: formId, quantity, source: this } }));
  }

  #sync(event) {
    if (event.detail.source === this || event.detail.form !== this.getAttribute('form')) return;
    if (this.#input && Number(this.#input.value) !== event.detail.quantity) {
      this.#input.value = event.detail.quantity;
      this.#renderButton(event.detail.quantity);
    }
  }

  #renderButton(quantity) {
    const button = document.querySelector(`#${CSS.escape(this.getAttribute('form'))} .hy-pdp-atc`);
    const label = button?.querySelector('.hy-pdp-atc__label');
    if (!label || button.disabled || button.classList.contains('is-added')) return;
    label.textContent = button.dataset.label;
    if (quantity <= 1) return;

    // Gesamtpreis als eigenes Element: mobil klein in zweiter Zeile, ab Desktop mit „ · “ in einer Zeile
    const total = document.createElement('span');
    const separator = document.createElement('span');
    total.className = 'hy-pdp-atc__total';
    separator.className = 'hy-pdp-atc__separator';
    separator.textContent = ' · ';
    total.append(separator, formatMoney(lineTotalCents(Number(this.getAttribute('unit-price')), quantity)));
    label.append(total);
  }
}

/* Gratisversand-Balken: rechnet Warenkorb plus gewählte Menge, aktualisiert sich bei cart:change, cart:refresh und
   beim Zurückkehren aus dem Browser-Cache. Nach dem Hinzufügen zählt nur der Warenkorb, bis die Menge geändert wird
   oder die Variante (z. B. im Drawer) wieder aus dem Warenkorb verschwindet. */
class HyPdpShippingBar extends HTMLElement {
  #abortController;
  #quantity = 1;
  #includeSelection = true;
  #quantityInCartAfterAdd = 0;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    document.addEventListener(QUANTITY_EVENT, (event) => {
      if (event.detail.form !== this.getAttribute('form')) return;
      if (event.detail.quantity !== this.#quantity) this.#includeSelection = true;
      this.#quantity = event.detail.quantity;
      this.#render();
    }, { signal });
    document.addEventListener('variant:add', (event) => {
      if (event.target.closest?.('form')?.getAttribute('id') !== this.getAttribute('form')) return;
      this.#includeSelection = false;
      this.#quantityInCartAfterAdd = event.detail?.cart ? variantQuantityInCart(event.detail.cart, this.#variantId) : 0;
      this.#render();
    }, { signal });
    document.addEventListener('cart:change', (event) => {
      if (event.detail?.cart) this.#applyCart(event.detail.cart);
    }, { signal });
    document.addEventListener('cart:refresh', () => this.#refreshCart(), { signal });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.#refreshCart();
    }, { signal });

    this.#quantity = readQuantity(this.getAttribute('form'));
    this.#render();
  }

  get #variantId() {
    return document.getElementById(this.getAttribute('form'))?.elements.namedItem('id')?.value;
  }

  #applyCart(cart) {
    this.setAttribute('cart-total', shippableCartTotalCents(cart));
    if (!this.#includeSelection && variantQuantityInCart(cart, this.#variantId) < this.#quantityInCartAfterAdd) {
      this.#includeSelection = true;
    }
    this.#render();
  }

  async #refreshCart() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!response.ok) throw new Error(`cart.js ${response.status}`);
      this.#applyCart(await response.json());
    } catch (error) {
      console.warn('hy-pdp-shipping-bar: Warenkorb konnte nicht aktualisiert werden', error);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #number(name) {
    return Number(this.getAttribute(name)) || 0;
  }

  #render() {
    const threshold = toPresentmentCents(this.#number('threshold'));
    const cartTotal = this.#number('cart-total');
    const added = this.#includeSelection ? lineTotalCents(this.#number('unit-price'), this.#quantity) : 0;
    const remaining = remainingForFreeShipping(threshold, cartTotal, added);
    const text = this.querySelector('.hy-pdp-shipping-bar__text');
    const fill = this.querySelector('.hy-pdp-shipping-bar__fill');

    if (text) {
      text.innerHTML = remaining > 0
        ? this.getAttribute('unreached-message').replace('{{ amount }}', formatMoney(remaining))
        : this.getAttribute('reached-message');
    }
    if (fill) fill.style.width = `${Math.round(freeShippingProgress(threshold, cartTotal, added) * 100)}%`;
  }
}

/* Rückmeldung nach dem Hinzufügen: Button zeigt kurz „Hinzugefügt“, Warenkorb-Symbol im Header hüpft. */
document.addEventListener('variant:add', (event) => {
  const form = event.target.closest?.('form');
  const button = form?.querySelector('.hy-pdp-atc') || (form && document.querySelector(`.hy-pdp-atc[form="${CSS.escape(form.getAttribute('id'))}"]`));
  if (!button) return;

  const label = button.querySelector('.hy-pdp-atc__label');
  button.classList.add('is-added');
  if (label) label.textContent = `${button.dataset.addedLabel} ✓`;
  setTimeout(() => {
    button.classList.remove('is-added');
    if (label) label.textContent = button.dataset.label;
    document.querySelector(`hy-pdp-quantity[form="${CSS.escape(form.getAttribute('id'))}"]`)?.dispatchEvent(new Event('change'));
  }, ADDED_FEEDBACK_MS);

  document.querySelectorAll('.header a[href$="/cart"], .header cart-count, .header__icon-list a[href*="cart"]').forEach((icon) => {
    icon.classList.remove('hy-pdp-bump');
    void icon.offsetWidth;
    icon.classList.add('hy-pdp-bump');
    setTimeout(() => icon.classList.remove('hy-pdp-bump'), CART_BUMP_MS);
  });
});

if (!customElements.get('hy-pdp-quantity')) customElements.define('hy-pdp-quantity', HyPdpQuantity);
if (!customElements.get('hy-pdp-shipping-bar')) customElements.define('hy-pdp-shipping-bar', HyPdpShippingBar);

/* Klick auf eine Wirkungs-Kachel öffnet das Akkordeon „Wirkung“ und scrollt dorthin. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-hy-pdp-open-accordion]');
  if (!trigger) return;
  const target = document.querySelector(trigger.getAttribute('href'));
  const details = target?.querySelector('details');
  if (!details) return;
  event.preventDefault();
  details.open = true;
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

/* Sticky-Leiste: sichtbar, sobald der Hauptbutton nach oben aus dem Bild gescrollt ist; Menge synchron zum Haupt-Stepper. */
class HyPdpSticky extends HTMLElement {
  #abortController;
  #observer;

  connectedCallback() {
    const formId = this.getAttribute('form');
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('click', (event) => {
      const step = event.target.closest('[data-step]');
      if (step) this.#changeQuantity(Number(step.dataset.step));
    }, { signal });

    document.addEventListener(QUANTITY_EVENT, (event) => {
      if (event.detail.form === formId) this.#renderQuantity(event.detail.quantity);
    }, { signal });

    const mainButton = document.querySelector(`#${CSS.escape(formId)} .hy-pdp-atc`);
    if (mainButton) {
      this.#observer = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.#observer.observe(mainButton);
    }

    this.#renderQuantity(readQuantity(formId));
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
  }

  #changeQuantity(delta) {
    const input = document.querySelector(`#${CSS.escape(this.getAttribute('form'))} input[name="quantity"]`);
    if (!input) return;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const step = Number(input.step) || 1;
    input.value = Math.min(max, Math.max(min, readQuantity(this.getAttribute('form')) + delta * step));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #renderQuantity(quantity) {
    const output = this.querySelector('[data-quantity]');
    if (output) output.textContent = quantity;
    this.querySelector('[data-step="-1"]')?.toggleAttribute('disabled', quantity <= 1);
  }
}

if (!customElements.get('hy-pdp-sticky')) customElements.define('hy-pdp-sticky', HyPdpSticky);

/*
  Variantenwechsel: Das Theme (<product-rerender allow-partial-rerender>) ersetzt nur Blöcke mit bekannten Typen.
  hy-price wird hier komplett ersetzt; in hy-buy nur Versandbalken, Lagerstatus und <buy-buttons> – das <form> bleibt,
  damit VariantPicker und ProductForm ihre Formular-Referenz behalten (wie das Theme beim Block „buy-buttons“).
*/
const BUY_PARTS = ['hy-pdp-shipping-bar', '.hy-pdp-stock', 'buy-buttons'];

const replaceFromFragment = (current, fresh) => {
  if (current && fresh) current.replaceWith(fresh);
};

document.addEventListener('product:rerender', (event) => {
  const { htmlFragment, productChange } = event.detail || {};
  const form = event.target;
  if (!htmlFragment || productChange || !(form instanceof HTMLFormElement)) return;

  const scope = form.closest('product-rerender[allow-partial-rerender]');
  if (!scope) return;

  scope.querySelectorAll('[data-block-type="hy-price"], [data-block-type="hy-buy"]').forEach((block) => {
    const selector = `[data-block-type="${block.dataset.blockType}"][data-block-id="${CSS.escape(block.dataset.blockId)}"]`;
    const freshBlock = htmlFragment.querySelector(selector);
    if (!freshBlock) return;

    if (block.dataset.blockType === 'hy-price') {
      replaceFromFragment(block, freshBlock);
      return;
    }

    const quantity = readQuantity(form.getAttribute('id'));
    BUY_PARTS.forEach((part) => replaceFromFragment(block.querySelector(part), freshBlock.querySelector(part)));

    const quantityInput = block.querySelector('input[name="quantity"]');
    if (quantityInput && quantity > 1) {
      quantityInput.value = quantity;
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}, true);
